import React from 'react'
import {
  ListItem,
  ListItemText,
  Box,
  TextField,
  IconButton,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'

interface StatusListItemProps {
  statusName: string
  isDefault: boolean
  isEditing: boolean
  renameInput: string
  onRenameInputChange: (val: string) => void
  onInitiateRename: (oldStatus: string) => void
  onCancelRename: () => void
  onStartEdit: (status: string) => void
  isLoading: boolean
  error: string | null
}

export const StatusListItem: React.FC<StatusListItemProps> = ({
  statusName,
  isDefault,
  isEditing,
  renameInput,
  onRenameInputChange,
  onInitiateRename,
  onCancelRename,
  onStartEdit,
  isLoading,
  error,
}) => {
  return (
    <ListItem
      divider
      sx={{
        '&:last-child': { borderBottom: 'none' },
        py: 0.75,
        px: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderRadius: '8px',
        mb: 0.5,
        transition: 'background-color 0.2s ease',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      {isEditing ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', gap: 1.5, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              value={renameInput}
              onChange={(e) => onRenameInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  onInitiateRename(statusName)
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  onCancelRename()
                }
              }}
              autoFocus
              error={Boolean(error)}
              helperText={
                error
                  ? error
                  : renameInput.length >= 25
                  ? 'Status cannot exceed 25 characters.'
                  : undefined
              }
              FormHelperTextProps={{
                sx: {
                  margin: '4px 0 0 !important',
                  fontSize: '0.75rem',
                  color: error ? 'error.main' : 'warning.main',
                },
              }}
              inputProps={{ maxLength: 25 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  height: 32,
                  fontSize: '0.875rem',
                  bgcolor: 'background.paper',
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, height: 32, alignItems: 'center' }}>
              <IconButton
                size="small"
                onClick={() => onInitiateRename(statusName)}
                disabled={isLoading || !renameInput.trim() || Boolean(error)}
                sx={{
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'primary.lighter' },
                }}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={onCancelRename}
                disabled={isLoading}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {!error && renameInput.length < 25 && renameInput.trim() && renameInput.trim() !== statusName && (
            <Typography
              variant="caption"
              sx={{
                color: 'warning.main',
                fontSize: '0.75rem',
                fontWeight: 500,
                mt: 0.5,
                display: 'block',
              }}
            >
              All leads with this status will be updated.
            </Typography>
          )}
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: isDefault ? 'text.disabled' : 'primary.main',
                opacity: 0.8,
              }}
            />
            <ListItemText
              primary={statusName}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: 500,
                color: isDefault ? 'text.secondary' : 'text.primary',
              }}
              sx={{ m: 0 }}
            />
          </Box>
          {isDefault ? (
            <Box
              sx={{
                fontSize: '0.7rem',
                bgcolor: 'action.selected',
                color: 'text.secondary',
                px: 1,
                py: 0.25,
                borderRadius: '10px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Default
            </Box>
          ) : (
            <IconButton
              edge="end"
              size="small"
              onClick={() => onStartEdit(statusName)}
              disabled={isLoading}
              sx={{
                color: 'text.secondary',
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <EditIcon fontSize="small" sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          )}
        </>
      )}
    </ListItem>
  )
}
